import React, { useEffect, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import {
  resolveGoogleClientId,
  shouldUseNativeGoogleSignIn,
  signInWithGoogleForApp,
} from '../../lib/googleAuth';
import { isIosLoginOnlyApp } from '../../lib/platform';

export function useGoogleClientId() {
  const envId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
  const [clientId, setClientId] = useState(envId);

  useEffect(() => {
    if (envId) return;
    void resolveGoogleClientId().then((id) => {
      if (id) setClientId(id);
    });
  }, [envId]);

  return clientId;
}

/**
 * Renders Google Sign-In on web (GIS) or a native button in the Capacitor app.
 * Hidden when no Google client id is configured.
 */
export default function GoogleAuthButton({ onCredential, onError, text = 'continue_with' }) {
  const clientId = useGoogleClientId();
  const [loading, setLoading] = useState(false);
  const native = shouldUseNativeGoogleSignIn();

  // Desktop / Custom-Tab fallback may stash the token in sessionStorage.
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem('ef_pending_google_id_token');
      if (pending && pending.length > 20) {
        sessionStorage.removeItem('ef_pending_google_id_token');
        onCredential(pending);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  if (!clientId || isIosLoginOnlyApp()) return null;

  const handleNative = async () => {
    setLoading(true);
    try {
      const credential = await signInWithGoogleForApp(clientId);
      onCredential(credential);
    } catch (err) {
      if (onError) {
        onError(err instanceof Error ? err : new Error('Google sign-in failed.'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (native) {
    return (
      <button
        type="button"
        onClick={() => void handleNative()}
        disabled={loading}
        className="flex w-full items-center justify-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-800 shadow-sm transition hover:bg-zinc-50 disabled:opacity-60"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {loading ? 'Signing in…' : 'Continue with Google'}
      </button>
    );
  }

  return (
    <div className="flex justify-center">
      <GoogleLogin
        text={text}
        shape="pill"
        size="large"
        width="320"
        useOneTap={false}
        ux_mode="popup"
        auto_select={false}
        onSuccess={(resp) => {
          if (resp && resp.credential) {
            onCredential(resp.credential);
          } else if (onError) {
            onError(new Error('Google did not return a credential.'));
          }
        }}
        onError={() => {
          if (onError) onError(new Error('Google sign-in was cancelled or failed.'));
        }}
      />
    </div>
  );
}

/** @deprecated use GoogleAuthButton visibility — kept for existing imports */
export const GOOGLE_ENABLED = !!(process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
